import { MfeReactComponent } from '@carvana/microfrontend-react';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Blocker, useBlocker, useLocation } from 'react-router-dom';
import { Step } from '|/app/services';
import telemetryService from '|/app/services/telemetry/telemetry-service';
import { DataDogTeamTags } from '|/app/services/telemetry/telemetry-types';
import { useBrand } from '|/brand';
import ErrorPage from '|/components/base/error';
import Loading from '|/components/base/loading';
import {
  DevicePlatformContext,
  PageEventContext,
  useNavigationAnalyticsContext,
  useNavigationBlockContext
} from '|/context';
import PurchaseContext from '|/context/purchase-context';
import { claimReactError } from '|/helpers/react-error-reporting';
import { loadVerifxMfe } from '|/hooks/use-global-mfe-config';
import useNavigateAwayFromPurchase from '|/hooks/use-navigate-away-from-purchase';
import useNavigateInPurchase from '|/hooks/use-navigate-in-purchase';
import useNavigateToNextStep from '|/hooks/use-navigate-to-next-step';
import { LeaveGuard, VerifxLeaveGuardContext } from './verifx-leave-guard-context';

const VERIFX_MODULE = 'PurchaseVerificationsModule';
const MFE_LOAD_TIMEOUT_MS = 30000;

const UNBLOCKED_BLOCKER: Blocker = {
  state: 'unblocked',
  proceed: undefined,
  reset: undefined,
  location: undefined
};

const VerifxMfeRoute: React.FC<{ step: Step | null }> = ({ step }) => {
  const { vehicleId } = useContext(PurchaseContext);
  const devicePlatform = useContext(DevicePlatformContext);
  const pageEventContext = useContext(PageEventContext);
  const { identity } = useBrand();
  const navigateToNextStep = useNavigateToNextStep();
  const navigateInPurchase = useNavigateInPurchase();
  const navigateAwayFromPurchase = useNavigateAwayFromPurchase();
  const { addBlocker, removeBlocker } = useNavigationBlockContext();
  const { addOpenModal, removeOpenModal, trackBlockedNavigation } = useNavigationAnalyticsContext();

  // The MFE's leave-this-step warning (KIQ): while it asks, every navigation — PUSH or POP — is held
  // until the user picks leave (proceed) or stay (reset). This is the only useBlocker on the router;
  // react-router consults just the last one registered, and at most one verifx step is mounted.
  const [isBlocking, setIsBlocking] = useState(false);
  const blocker = useBlocker(isBlocking);
  const [mfeBlocker, setMfeBlocker] = useState<Blocker>(UNBLOCKED_BLOCKER);

  const setShouldBlock = useCallback((shouldBlock: boolean) => {
    setIsBlocking(shouldBlock);
    if (!shouldBlock) setMfeBlocker(UNBLOCKED_BLOCKER);
  }, []);

  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    const tracked = trackBlockedNavigation(blocker);
    // react-router hands back a NEW blocker object on every state change, so the one we push to the
    // MFE stays 'blocked' forever once the user resolves it. The MFE opens its exit warning off
    // `blocker.state === 'blocked'`, so a stale object re-opens that modal on a later render with an
    // already-spent proceed(). Clear it as the MFE resolves.
    const clearThen = (fn?: () => void) => () => {
      setMfeBlocker(UNBLOCKED_BLOCKER);
      fn?.();
    };
    setMfeBlocker({
      ...tracked,
      proceed: clearThen(tracked.proceed),
      reset: clearThen(tracked.reset)
    } as Blocker);
  }, [blocker, trackBlockedNavigation]);

  const registerLeaveGuard = useContext(VerifxLeaveGuardContext);
  const location = useLocation();

  // A host-initiated close (the task sheet's "x") is not a router navigation, so useBlocker never
  // sees it. While the MFE is blocking, hand it a blocked blocker so it opens the same exit warning.
  const requestLeave = useCallback<LeaveGuard['requestLeave']>(
    onProceed => {
      if (!isBlocking) return false;
      setMfeBlocker({
        state: 'blocked',
        location,
        proceed: () => {
          setMfeBlocker(UNBLOCKED_BLOCKER);
          onProceed();
        },
        reset: () => {
          setMfeBlocker(UNBLOCKED_BLOCKER);
        }
      });
      return true;
    },
    [isBlocking, location]
  );

  const leaveGuard = useMemo<LeaveGuard>(
    () => ({ isBlocking: () => isBlocking, requestLeave }),
    [isBlocking, requestLeave]
  );

  useEffect(() => registerLeaveGuard?.(leaveGuard), [registerLeaveGuard, leaveGuard]);

  // Back-close bridge for MFE modals (TXCC-9639) — mirrors the host useBrowserBackClose so Verifx
  // modals join the same sentinel set. The MFE ships separately and cannot be assumed to pair its
  // calls perfectly; addBlocker/removeBlocker are keyed and idempotent, and the coordinator derives
  // the sentinel's lifecycle from that same registry, so an unbalanced call can't drift the two apart.
  const registerBackClose = useCallback(
    (key: string, onClose: () => void) => {
      addBlocker(key, onClose);
    },
    [addBlocker]
  );

  const unregisterBackClose = useCallback(
    (key: string) => {
      removeBlocker(key);
    },
    [removeBlocker]
  );

  // One prop so it is obvious these four belong together. Memoized: the MFE spreads this into
  // MfeNavigationContext, and a fresh object each render would churn the context for every consumer
  // inside the MFE. Not called "backstop" — addOpenModal/removeOpenModal are permanent presence
  // instrumentation that must survive the pp-backstop cleanup.
  const modalNav = useMemo(
    () => ({
      registerBackClose,
      unregisterBackClose,
      addOpenModal,
      removeOpenModal
    }),
    [registerBackClose, unregisterBackClose, addOpenModal, removeOpenModal]
  );

  useEffect(() => {
    loadVerifxMfe();
  }, []);

  return (
    <MfeReactComponent
      module={VERIFX_MODULE}
      component="default"
      step={step}
      vehicleId={vehicleId}
      devicePlatform={devicePlatform}
      source={identity.mfeBrand}
      theme={identity.mfeBrand}
      pageEventContext={pageEventContext}
      onNavigateToNextStep={navigateToNextStep}
      onNavigateInPurchase={navigateInPurchase}
      onNavigateAwayFromPurchase={navigateAwayFromPurchase}
      blocker={mfeBlocker}
      loading={<Loading />}
      setShouldBlock={setShouldBlock}
      modalNav={modalNav}
      fallback={{ Component: <ErrorPage />, timeout: MFE_LOAD_TIMEOUT_MS }}
      onComponentError={({ id, error }: { id: string; error?: Error }) => {
        if (!claimReactError(error)) return;
        telemetryService.trackError({
          name: 'verifx-mfe:error',
          error: new Error(`Verifx MFE component failed: ${id}`, { cause: error }),
          context: {
            customType: 'errorBoundary',
            team: DataDogTeamTags.Verifx,
            mfe: VERIFX_MODULE,
            mfeComponentId: id
          }
        });
      }}
    />
  );
};

export default VerifxMfeRoute;
